import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Copy, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { AnswerFeedback } from '@/features/feedback';
import { isRunning, type Turn } from '../model/conversation';
import { AnswerText } from './AnswerText';
import { LiveDraft } from './LiveDraft';
import { pulseRingSx, riseSx } from './motion';
import { StepsDisclosure } from './StepsDisclosure';

type Props = { turn: Turn; onPage: (page: number) => void };

/** An answer from one report: the text with "p.N" marks that move the viewer, then Copy and feedback. */
export function ReportAnswer({ turn, onPage }: Props) {
  const running = isRunning(turn);
  const { answer } = turn;
  const [copied, setCopied] = useState(false);

  return (
    <Stack direction="row" sx={{ gap: 1.25, alignItems: 'flex-start' }}>
      <Box aria-hidden sx={[markSx, running && pulseRingSx]}>
        <Sparkles size={12} strokeWidth={2} />
      </Box>
      <Stack sx={{ gap: 1.25, minWidth: 0, flexGrow: 1 }}>
        <StepsDisclosure steps={turn.steps} isRunning={running} startedAt={turn.startedAt} latencyMs={answer?.latencyMs} />
        {answer ? null : <LiveDraft text={turn.draft} isRunning={running} />}
        {answer?.status === 'refused' ? <Alert severity="warning" variant="outlined">{answer.text}</Alert> : null}
        {answer && answer.status !== 'refused' ? (
          <Stack sx={{ gap: 1, ...riseSx }}>
            <AnswerText text={answer.text} citations={answer.citations} onPage={onPage} />
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.25, ml: -1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                startIcon={<Copy size={13} />}
                onClick={() => { void navigator.clipboard.writeText(answer.text.replace(/\s*\[\d+\]/g, '')).then(() => { setCopied(true); }); }}
                sx={{ color: 'text.secondary', minHeight: 28, fontSize: '0.75rem' }}
              >
                Copy
              </Button>
              <AnswerFeedback about={{ question: turn.question, answer: answer.text, route: answer.route, citations: answer.citations }} />
            </Stack>
          </Stack>
        ) : null}
        {turn.error ? <Alert severity="error" variant="outlined">{turn.error}</Alert> : null}
        {turn.stopped ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stopped before the answer finished.</Typography> : null}
      </Stack>
      <Snackbar open={copied} autoHideDuration={2500} onClose={() => { setCopied(false); }} message="Answer copied." />
    </Stack>
  );
}

const markSx = {
  width: 22,
  height: 22,
  flexShrink: 0,
  borderRadius: 0.75,
  display: 'grid',
  placeItems: 'center',
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
} as const;
